import { useEffect, useRef, useState } from "react";
import { Download, Share, X } from "lucide-react";

function checkStandalone() {
  return (
    (typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(display-mode: standalone)").matches) ||
    window.navigator.standalone === true
  );
}

function checkIOS() {
  const ua = window.navigator.userAgent || "";
  const iOSDevice = /iphone|ipad|ipod/i.test(ua);
  const iPadOS = navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  return iOSDevice || iPadOS;
}

// Install control. Always visible unless the app is already running installed
// (standalone). Uses the native prompt when the browser offers it; otherwise
// shows clear "Add to Home Screen" instructions (iOS/Safari, or Android/desktop
// fallback) instead of hiding the option.
export function InstallAppButton({ variant = "desktop" }) {
  const [deferred, setDeferred] = useState(null);
  const [standalone, setStandalone] = useState(checkStandalone());
  const [help, setHelp] = useState(null); // null | "ios" | "generic"
  const ios = checkIOS();
  const wrapRef = useRef(null);

  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => { setDeferred(null); setStandalone(true); setHelp(null); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    const mq = window.matchMedia("(display-mode: standalone)");
    const onChange = () => setStandalone(checkStandalone());
    mq.addEventListener?.("change", onChange);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
      mq.removeEventListener?.("change", onChange);
    };
  }, []);

  useEffect(() => {
    if (!help) return;
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setHelp(null); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [help]);

  if (standalone) return null; // already installed — nothing to do

  const doInstall = async () => {
    if (deferred) {
      deferred.prompt();
      try { await deferred.userChoice; } catch { /* dismissed */ }
      setDeferred(null);
      return;
    }
    setHelp(ios ? "ios" : "generic");
  };

  const label = "Install app";
  const isDialog = !deferred; // clicking will open instructions rather than a native prompt

  const helpPanel = help && (
    <div
      role="dialog"
      aria-label="How to install this app"
      className="absolute right-0 top-full mt-2 w-64 z-50 rounded-xl border border-donkey-orange/30 bg-donkey-deep p-4 shadow-[0_10px_40px_rgba(0,0,0,0.5)]"
      data-testid="install-help"
    >
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-semibold text-donkey-warm">Add to Home Screen</p>
        <button onClick={() => setHelp(null)} aria-label="Close" className="text-donkey-grey hover:text-donkey-warm">
          <X className="w-4 h-4" />
        </button>
      </div>
      {help === "ios" ? (
        <p className="text-xs text-donkey-grey mt-2 leading-relaxed" data-testid="install-help-ios">
          Open this site in Safari, tap the <Share className="inline w-3.5 h-3.5 -mt-0.5 text-donkey-orange" />{" "}
          <span className="text-donkey-warm">Share</span> icon, then choose{" "}
          <span className="text-donkey-warm">“Add to Home Screen”</span>.
        </p>
      ) : (
        <p className="text-xs text-donkey-grey mt-2 leading-relaxed" data-testid="install-help-generic">
          Open your browser menu (⋮) and choose{" "}
          <span className="text-donkey-warm">“Install app”</span> or{" "}
          <span className="text-donkey-warm">“Add to Home screen”</span>.
        </p>
      )}
    </div>
  );

  if (variant === "mobile") {
    return (
      <div className="relative" ref={wrapRef}>
        <button
          onClick={doInstall}
          className="flex items-center gap-2 text-donkey-warm outline-none focus-visible:ring-2 focus-visible:ring-donkey-orange/70 rounded-md"
          aria-label={label}
          aria-haspopup={isDialog ? "dialog" : undefined}
          data-testid="install-app-mobile"
        >
          <Download className="w-4 h-4 text-donkey-orange" /> {label}
        </button>
        {helpPanel}
      </div>
    );
  }

  return (
    <div className="relative" ref={wrapRef}>
      <button
        onClick={doInstall}
        className="inline-flex items-center gap-1.5 text-sm text-donkey-grey hover:text-donkey-warm transition-colors px-2 py-1.5 rounded-md outline-none focus-visible:ring-2 focus-visible:ring-donkey-orange/70 focus-visible:ring-offset-2 focus-visible:ring-offset-donkey-black"
        aria-label={label}
        aria-haspopup={isDialog ? "dialog" : undefined}
        data-testid="install-app-desktop"
      >
        <Download className="w-4 h-4 text-donkey-orange" /> {label}
      </button>
      {helpPanel}
    </div>
  );
}
